import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheckCircle, FiShield, FiStar, FiAward, FiClock, FiHeart, FiUsers, FiTrendingUp } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import HeroSection from '../components/HeroSection';
import dataService from '../services/dataService';
import SEO from '../components/SEO';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await dataService.getServices();
        setServices(data);
      } catch (error) {
        console.error('Failed to load services:', error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, []);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <SEO
        pageType="services"
        title="Advanced Treatment Solutions | JIVA NADI Therapy Center"
        description="Experience cutting-edge therapies combining ancient wisdom with modern science. Non-invasive treatments for optimal healing and wellness."
        keywords="acupuncture therapy, chiropractic care, cupping massage, naturopathy, pain relief treatments, Mumbai therapy center"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="Transform Your Health with Expert Care"
        subtitle="Premium Healthcare Excellence"
        description="Experience world-class acupuncture and chiropractic services tailored to your unique needs. Our certified practitioners combine ancient wisdom with modern techniques to deliver lasting relief and optimal wellness."
        image="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop"
        badge="Premium Healthcare Excellence"
        primaryButton={{
          text: "Book Your Consultation",
          href: "tel:9581556660"
        }}
        secondaryButton={{
          text: "Learn More",
          href: "#services"
        }}
        trustIndicators={[
          { value: "15+", label: "Years Experience" },
          { value: "30K+", label: "Happy Patients" },
          { value: "100%", label: "Success Rate" }
        ]}
      />

      {/* Core Pillars - Enhanced */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-bold uppercase tracking-widest text-sm">Our Foundation</span>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mt-4 mb-6">
              Core <span className="text-emerald-600 italic">Principles</span>
            </h2>
            <div className="w-24 h-1 bg-emerald-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white to-slate-50 p-10 rounded-3xl shadow-xl shadow-emerald-900/5 border border-emerald-50 hover:shadow-2xl hover:-translate-y-2 transition-all group"
            >
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center text-emerald-600 mb-8 group-hover:bg-gradient-to-br group-hover:from-emerald-600 group-hover:to-teal-600 group-hover:text-white transition-all duration-500">
                <SafeIcon icon={FiShield} className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-4">Non-Invasive</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                100% natural treatments without surgery or heavy medication.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white to-slate-50 p-10 rounded-3xl shadow-xl shadow-emerald-900/5 border border-emerald-50 hover:shadow-2xl hover:-translate-y-2 transition-all group"
            >
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center text-emerald-600 mb-8 group-hover:bg-gradient-to-br group-hover:from-emerald-600 group-hover:to-teal-600 group-hover:text-white transition-all duration-500">
                <SafeIcon icon={FiHeart} className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-4">Holistic Approach</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Treating the root cause of pain, not just the symptoms.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white to-slate-50 p-10 rounded-3xl shadow-xl shadow-emerald-900/5 border border-emerald-50 hover:shadow-2xl hover:-translate-y-2 transition-all group"
            >
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center text-emerald-600 mb-8 group-hover:bg-gradient-to-br group-hover:from-emerald-600 group-hover:to-teal-600 group-hover:text-white transition-all duration-500">
                <SafeIcon icon={FiAward} className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-4">Expert Care</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Led by Dr. Saurabh Singh with 15+ years of clinical experience.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white to-slate-50 p-10 rounded-3xl shadow-xl shadow-emerald-900/5 border border-emerald-50 hover:shadow-2xl hover:-translate-y-2 transition-all group"
            >
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center text-emerald-600 mb-8 group-hover:bg-gradient-to-br group-hover:from-emerald-600 group-hover:to-teal-600 group-hover:text-white transition-all duration-500">
                <SafeIcon icon={FiTrendingUp} className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-4">Modern Tech</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Combining ancient wisdom with state-of-the-art diagnostic tools.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advanced Treatments - Redesigned */}
      <section className="py-32 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,#10b981_0%,transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,#059669_0%,transparent_50%)]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-2 bg-emerald-100/80 border border-emerald-200 px-6 py-2 rounded-full mb-8 backdrop-blur-sm">
              <SafeIcon icon={FiAward} className="text-emerald-600 w-4 h-4" />
              <span className="text-emerald-700 font-semibold uppercase tracking-wide text-sm">Evidence-Based Therapies</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-black text-gray-900 mb-8 leading-tight">
              Advanced <span className="text-emerald-600 italic bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Treatment Solutions</span>
            </h2>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-light">
              Experience cutting-edge therapies that combine traditional wisdom with modern science for optimal healing and wellness.
            </p>
            <div className="flex justify-center mt-12">
              <div className="w-32 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {services.map((treatment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="group relative"
              >
                <div className="relative bg-white rounded-3xl shadow-xl shadow-emerald-900/10 border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-500 transform hover:-translate-y-3">
                  {/* Image Container */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={treatment.image}
                      alt={treatment.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-emerald-950/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>

                    {/* Floating Badge */}
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-emerald-700 shadow-lg">
                      Advanced
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <h3 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors duration-300">
                      {treatment.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-6 line-clamp-3">
                      {treatment.description}
                    </p>

                    {/* Features List */}
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <SafeIcon icon={FiCheckCircle} className="text-emerald-500 w-4 h-4 flex-shrink-0" />
                        <span>Non-invasive approach</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <SafeIcon icon={FiCheckCircle} className="text-emerald-500 w-4 h-4 flex-shrink-0" />
                        <span>Evidence-based results</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <SafeIcon icon={FiCheckCircle} className="text-emerald-500 w-4 h-4 flex-shrink-0" />
                        <span>Personalized care</span>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 group/btn"
                    >
                      <span>Explore Treatment</span>
                      <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                    </motion.button>
                  </div>

                  {/* Hover Overlay Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl p-12 text-white shadow-2xl"
            >
              <h3 className="text-3xl font-black mb-4">Ready to Start Your Healing Journey?</h3>
              <p className="text-emerald-100 mb-8 text-lg">Book a consultation and discover which treatment is right for you.</p>
              <motion.a
                href="tel:9581556660"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-3 bg-white text-emerald-600 font-bold py-4 px-8 rounded-2xl hover:bg-gray-50 transition-all shadow-lg"
              >
                <span>Call Now: 9581556660</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section - New */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-bold uppercase tracking-widest text-sm">How It Works</span>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mt-4 mb-6">
              Your <span className="text-emerald-600 italic">Journey to Wellness</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Experience a seamless path from consultation to recovery with our patient-centered approach.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", desc: "Comprehensive assessment of your health concerns and goals." },
              { step: "02", title: "Personalized Plan", desc: "Custom treatment strategy tailored to your specific needs." },
              { step: "03", title: "Therapy Sessions", desc: "Expert-guided treatments using advanced techniques." },
              { step: "04", title: "Ongoing Support", desc: "Continuous monitoring and adjustments for optimal results." }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-black text-2xl mb-6 mx-auto group-hover:bg-emerald-600 group-hover:text-white transition-all">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dedication Banner - Enhanced */}
      <section className="py-32 bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:32px_32px]"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center space-x-2 bg-emerald-800/50 border border-emerald-700 px-6 py-2 rounded-full mb-8">
              <SafeIcon icon={FiAward} className="text-emerald-400" />
              <span className="text-sm font-bold uppercase tracking-widest text-emerald-300">Excellence in Healthcare</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-black mb-12 leading-tight">
              Committed to Your <span className="text-emerald-400 italic">Complete Recovery</span>
            </h2>
            <p className="text-xl text-emerald-100/80 mb-16 max-w-4xl mx-auto leading-relaxed">
              Our dedication to excellence is reflected in the transformative success stories of patients who have achieved lasting wellness through Jiva Nadi's comprehensive care approach.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
              <div className="space-y-3">
                <div className="text-5xl font-black text-emerald-400">30K+</div>
                <div className="text-sm uppercase font-bold tracking-widest text-emerald-100/60">Successful Treatments</div>
              </div>
              <div className="space-y-3">
                <div className="text-5xl font-black text-emerald-400">15+</div>
                <div className="text-sm uppercase font-bold tracking-widest text-emerald-100/60">Years of Excellence</div>
              </div>
              <div className="space-y-3">
                <div className="text-5xl font-black text-emerald-400">4.9★</div>
                <div className="text-sm uppercase font-bold tracking-widest text-emerald-100/60">Patient Satisfaction</div>
              </div>
              <div className="space-y-3">
                <div className="text-5xl font-black text-emerald-400">100%</div>
                <div className="text-sm uppercase font-bold tracking-widest text-emerald-100/60">Natural Healing</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;